import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { celebrate } from '../lib/celebrate';
import { CheckCircle2, XCircle, Loader2, Copy, Gift, Twitter, MessageCircle, Mail } from 'lucide-react';

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState('checking');
  const [ref, setRef] = useState(null);
  const tries = useRef(0);
  const celebrated = useRef(false);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('session_id');
    if (!sid) { setState('error'); return; }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const res = await api.get(`/payments/status/${sid}`);
        if (res.data.payment_status === 'paid') { setState('paid'); return; }
        if (res.data.status === 'expired' || tries.current > 12) { setState('error'); return; }
      } catch (e) { if (tries.current > 12) { setState('error'); return; } }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  // The happy moment: confetti + fetch their referral code to prompt sharing.
  useEffect(() => {
    if (state !== 'paid' || celebrated.current) return;
    celebrated.current = true;
    setTimeout(() => celebrate(), 300);
    api.get('/referral/me').then((r) => setRef(r.data)).catch(() => {});
  }, [state]);

  const shareUrl = ref ? `${window.location.origin}/app/upgrade?ref=${ref.code}` : '';
  const shareMsg = "I'm loving Larkwell — a gentle, ADHD-friendly AI day planner. Use my link for a discount on Pro:";
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Share link copied!'); }
    catch (e) { toast.error('Copy failed — select and copy manually.'); }
  };
  const shareTo = (network) => {
    const u = encodeURIComponent(shareUrl);
    const m = encodeURIComponent(shareMsg);
    const links = {
      x: `https://twitter.com/intent/tweet?text=${m}&url=${u}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareMsg + ' ' + shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent('You should try Larkwell')}&body=${encodeURIComponent(shareMsg + '\n\n' + shareUrl)}`,
    };
    if (network === 'email') { window.location.href = links.email; }
    else { window.open(links[network], '_blank', 'noopener,noreferrer'); }
  };

  return (
    <div className="min-h-dvh grid place-items-center bg-[hsl(var(--background))] p-6">
      <div className="text-center max-w-sm">
        {state === 'checking' && (<><Loader2 className="h-10 w-10 animate-spin mx-auto text-[hsl(var(--primary))]" /><p className="mt-4 font-600">Confirming your payment…</p></>)}
        {state === 'paid' && (<>
          <div className="h-16 w-16 rounded-3xl mx-auto grid place-items-center" style={{ background: 'hsl(var(--success) / 0.15)' }}><CheckCircle2 className="h-9 w-9" style={{ color: 'hsl(var(--success))' }} /></div>
          <h1 className="font-display text-2xl font-700 mt-4" data-testid="payment-success-heading">You're in!</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">Your purchase is confirmed. Welcome to Larkwell Pro — go make a gentle plan.</p>

          {ref && (
            <div className="mt-6 rounded-2xl border p-5 text-left bg-card" style={{ borderColor: 'hsl(var(--primary) / 0.4)' }} data-testid="success-referral-card">
              <div className="flex items-center gap-2"><Gift className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} /><p className="text-sm font-600">Love it? Share the love</p></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">A friend gets <b>${ref.buyer_discount} off</b> their first purchase, and you earn <b>{ref.reward_credits} credits</b> each time one joins.</p>
              <div className="flex items-center gap-2 mt-3">
                <div className="flex-1 min-w-0 rounded-xl border border-[hsl(var(--border))] px-3 h-10 flex items-center bg-[hsl(var(--surface-2))]">
                  <span className="font-mono font-700 tracking-wide truncate" data-testid="success-referral-code">{ref.code}</span>
                </div>
                <Button onClick={copyShare} className="rounded-xl gap-1.5 h-10 shrink-0" style={{ background: 'hsl(var(--primary))' }} data-testid="success-copy-referral"><Copy className="h-4 w-4" /> Copy link</Button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[11px] text-[hsl(var(--muted-foreground))] mr-0.5">Share:</span>
                <button onClick={() => shareTo('x')} className="h-9 w-9 rounded-xl border border-[hsl(var(--border))] grid place-items-center hover:border-[hsl(var(--primary)/0.5)] transition-colors" aria-label="Share on X" data-testid="share-x"><Twitter className="h-4 w-4" /></button>
                <button onClick={() => shareTo('whatsapp')} className="h-9 w-9 rounded-xl border border-[hsl(var(--border))] grid place-items-center hover:border-[hsl(var(--primary)/0.5)] transition-colors" aria-label="Share on WhatsApp" data-testid="share-whatsapp"><MessageCircle className="h-4 w-4" style={{ color: 'hsl(var(--success))' }} /></button>
                <button onClick={() => shareTo('email')} className="h-9 w-9 rounded-xl border border-[hsl(var(--border))] grid place-items-center hover:border-[hsl(var(--primary)/0.5)] transition-colors" aria-label="Share by email" data-testid="share-email"><Mail className="h-4 w-4" /></button>
              </div>
            </div>
          )}

          <Button className="rounded-xl mt-6" style={{ background: 'hsl(var(--primary))' }} onClick={() => navigate('/app/today')} data-testid="success-continue-button">Go to Today</Button>
        </>)}
        {state === 'error' && (<>
          <div className="h-16 w-16 rounded-3xl mx-auto grid place-items-center" style={{ background: 'hsl(var(--secondary))' }}><Loader2 className="h-9 w-9 text-[hsl(var(--muted-foreground))]" /></div>
          <h1 className="font-display text-xl font-700 mt-4">Still processing</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">If you completed payment, it may take a moment. You can head back and it'll update shortly.</p>
          <Button className="rounded-xl mt-6" style={{ background: 'hsl(var(--primary))' }} onClick={() => navigate('/app/upgrade')} data-testid="success-back-button">Back to Upgrade</Button>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-dvh grid place-items-center bg-[hsl(var(--background))] p-6">
      <div className="text-center max-w-sm">
        <div className="h-16 w-16 rounded-3xl mx-auto grid place-items-center" style={{ background: 'hsl(var(--secondary))' }}><XCircle className="h-9 w-9 text-[hsl(var(--muted-foreground))]" /></div>
        <h1 className="font-display text-xl font-700 mt-4">Checkout canceled</h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-2">No worries — nothing was charged. You can upgrade whenever you're ready.</p>
        <Button className="rounded-xl mt-6" style={{ background: 'hsl(var(--primary))' }} onClick={() => navigate('/app/upgrade')} data-testid="cancel-back-button">Back to Upgrade</Button>
      </div>
    </div>
  );
}
